import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../Common/avatar';
import { useAuth } from '../../auth/useAuth';
import { useAuthStore, useEffectiveUser } from '../../stores/authStore';
import { SidebarItemRow } from './SidebarItemRow';

interface SidebarUserCardProps {
  collapsed: boolean;
}

const PROFILE_PATH = '/profil';
const MAX_NAME_LENGTH = 15;

const TAG_CLASS = 'inline-flex max-w-full items-center rounded-full px-1.5 py-px text-[10px] font-medium leading-4';

export const SidebarUserCard: React.FC<SidebarUserCardProps> = ({ collapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const effectiveUser = useEffectiveUser();
  const { user } = useAuthStore();
  const displayUser = effectiveUser ?? user;
  const logout = useAuthStore((state) => state.actions.logout);
  const { signOut } = useAuth();

  if (!displayUser) return null;

  const displayName = displayUser.name || displayUser.email || t('global.user');
  const roleLabel = displayUser.role ? t(`admin.users.role_${displayUser.role}`, displayUser.role) : null;
  const profileName = displayUser.profile?.name;
  const avatarSrc =
    displayUser.avatar_url && displayUser.avatar_url.trim().length > 0
      ? displayUser.avatar_url
      : '/default-persona-avatar.svg';
  const initial = (displayUser.email || 'U').charAt(0).toUpperCase();
  const active = location.pathname === PROFILE_PATH || location.pathname === '/profile';
  const profileLabel = t('sidebar.openProfile', { name: displayName });
  const shortName =
    displayName.length > MAX_NAME_LENGTH ? `${displayName.slice(0, MAX_NAME_LENGTH).trimEnd()}…` : displayName;

  const handleSignOut = async () => {
    // Demo users: clear session locally without going through Logto
    if (user?.role === 'demo') {
      localStorage.removeItem('demo_session_token');
      logout();
      window.location.href = '/';
      return;
    }
    try {
      await signOut(window.location.origin);
    } catch (error) {
      console.error('Error signing out from Logto:', error);
      logout();
      navigate('/', { replace: true });
    }
  };

  const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  const logoutButton = (
    <SidebarItemRow label={t('sidebar.logout')} showTooltip={collapsed} className={collapsed ? undefined : 'mt-2'}>
      {(rowRef) => (
        <button
          ref={rowRef}
          type="button"
          onClick={handleSignOut}
          aria-label={t('sidebar.logout')}
          title={collapsed ? undefined : t('sidebar.logout')}
          className={cn(
            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors',
            'hover:bg-destructive/10 hover:text-destructive',
            focusRing
          )}
        >
          <LogOut className="h-4 w-4" />
        </button>
      )}
    </SidebarItemRow>
  );

  return (
    <div className={cn('flex gap-1', collapsed ? 'flex-col items-center' : 'items-start')}>
      <SidebarItemRow label={displayName} showTooltip={collapsed} className={collapsed ? undefined : 'min-w-0 flex-1'}>
        {(rowRef) => (
          <Link
            ref={rowRef}
            to={PROFILE_PATH}
            aria-label={profileLabel}
            aria-current={active ? 'page' : undefined}
            data-tour="account-card"
            className={cn(
              'flex min-w-0 items-start gap-2.5 rounded-lg p-2 transition-colors',
              !collapsed && 'w-full',
              'hover:bg-muted/60',
              active && 'bg-primary/10',
              focusRing
            )}
          >
            <Avatar className="my-auto h-8 w-8 flex-shrink-0">
              <AvatarImage src={avatarSrc} alt="" />
              <AvatarFallback className="text-xs">{initial}</AvatarFallback>
            </Avatar>
            {!collapsed && (
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span
                  className={cn(
                    'truncate text-[13px] font-semibold leading-tight',
                    active ? 'text-primary' : 'text-foreground'
                  )}
                  title={shortName !== displayName ? displayName : undefined}
                >
                  {shortName}
                </span>
                {(roleLabel || profileName || displayUser.organization_is_demo) && (
                  <span className="mt-1 flex flex-wrap gap-1">
                    {roleLabel && <span className={cn(TAG_CLASS, 'bg-primary/10 text-primary')}>{roleLabel}</span>}
                    {profileName && (
                      <span className={cn(TAG_CLASS, 'truncate bg-muted text-muted-foreground')} title={profileName}>
                        {profileName}
                      </span>
                    )}
                    {displayUser.organization_is_demo && (
                      <span className={cn(TAG_CLASS, 'bg-amber-500/15 text-amber-700 dark:text-amber-400')}>
                        {t('sidebar.demoTag')}
                      </span>
                    )}
                  </span>
                )}
              </span>
            )}
          </Link>
        )}
      </SidebarItemRow>
      {logoutButton}
    </div>
  );
};
